import {Injectable} from '@angular/core';
import {AngularFireRemoteConfig} from '@angular/fire/compat/remote-config';

const featureFlags = {
  termsWarning: 'terms_warning',
  // DEV-1501: per-tenant rollout dial for the v6 multipart intake on
  // closecare-apis-services. Accepts boolean true (everyone), a
  // comma-separated company_id allowlist, or anything falsy (v4).
  // Shared Firebase project with web-b2c, so a single Remote Config
  // edit moves atestado.me and enviei.me together.
  newDocumentApi: 'new_document_api',
};

export type FeatureFlags = Record<keyof typeof featureFlags, FlagValue>;

/**
 * Pure predicate (exported for reuse + unit testing): is the company on the
 * new_document_api rollout? Mirrors the boolean inside resolveSickNoteEndpoint
 * so endpoint routing and UI gating share one source of truth.
 */
export const isNewDocumentApiEnabled = (flagValue: FlagValue, companyId: string | null): boolean => {
  if (!companyId) {
    return false;
  }
  if (flagValue === true) {
    return true;
  }
  if (typeof flagValue !== 'string' || flagValue.length === 0) {
    return false;
  }
  return flagValue.split(',').map((id) => id.trim()).filter(Boolean).includes(companyId);
};

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  featureFlags: FeatureFlags = {} as FeatureFlags;

  constructor(private remoteConfig: AngularFireRemoteConfig) {
    Object.keys(featureFlags).forEach(
      (key) => (this.featureFlags[key as keyof FeatureFlags] = false)
    );
  }

  async load(): Promise<void> {
    await this.remoteConfig.fetchAndActivate();

    const promises: Promise<[string, FlagValue]>[] = [];
    Object.keys(featureFlags).forEach((key) =>
      promises.push(this.getValue(key))
    );

    const result = await Promise.all(promises);
    result.forEach(
      ([key, value]) => (this.featureFlags[key as keyof FeatureFlags] = value)
    );
  }

  async getValue(key: string): Promise<[string, FlagValue]> {
    const flagName = featureFlags[key as keyof typeof featureFlags];
    const valueAsString = (
      await this.remoteConfig.getValue(flagName)
    ).asString();

    return [key, this.convertFromStringValue(valueAsString)];
  }

  convertFromStringValue(str: string) {
    if (str === 'true') {
      return true;
    }
    if (str === 'false') {
      return false;
    }

    return str;
  }

  isNewDocumentApiEnabledForCompany(companyId: string | null): boolean {
    return isNewDocumentApiEnabled(this.featureFlags.newDocumentApi, companyId);
  }
}

type FlagValue = boolean | string | null;
